import { useCallback, useRef, useState, type ReactNode } from 'react';
import { StepUpDialog } from './StepUpDialog';
import { ApiError } from '../../identity/types';

const STEP_UP_REQUIRED = 'STEP_UP_REQUIRED';

function isStepUpRequired(err: unknown): boolean {
  return err instanceof ApiError && err.code === STEP_UP_REQUIRED;
}

/**
 * Re-verification for sensitive security changes: proof is a one-time code emailed to the signed-in user.
 * `guard(action)` runs the action; if the server answers STEP_UP_REQUIRED it opens the dialog, and after a
 * successful verification retries the action once. Cancelling resolves undefined without running it again.
 */
export function useStepUpGuard() {
  const [open, setOpen] = useState(false);
  const pending = useRef<((verified: boolean) => void) | null>(null);

  const ask = useCallback(
    () =>
      new Promise<boolean>((resolve) => {
        pending.current = resolve;
        setOpen(true);
      }),
    [],
  );

  const finish = useCallback((verified: boolean) => {
    setOpen(false);
    pending.current?.(verified);
    pending.current = null;
  }, []);

  const guard = useCallback(
    async <T,>(action: () => Promise<T>): Promise<T | undefined> => {
      try {
        return await action();
      } catch (err) {
        if (!isStepUpRequired(err)) throw err;
        if (!(await ask())) return undefined;
        return await action();
      }
    },
    [ask],
  );

  const dialog: ReactNode = open ? <StepUpDialog onVerified={() => finish(true)} onCancel={() => finish(false)} /> : null;
  return { guard, dialog };
}

